import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, HeartPulse, Heart, Star, Puzzle, Clock, Drama } from "lucide-react";
import { LexiqueVivant } from "@/components/LexiqueVivant";
import { EmotionsLexique } from "@/components/EmotionsLexique";
import { CosmosLexique } from "@/components/CosmosLexique";
import { RacinesLexique, TempsLexique, MythesLexique } from "@/components/ExtraLexiques";

const LEXIQUES = [
  { key: "vivant", icon: HeartPulse, label: "tabVivant", Comp: LexiqueVivant },
  { key: "emotions", icon: Heart, label: "tabEmotions", Comp: EmotionsLexique },
  { key: "cosmos", icon: Star, label: "tabCosmos", Comp: CosmosLexique },
  { key: "racines", icon: Puzzle, label: "tabRacines", Comp: RacinesLexique },
  { key: "temps", icon: Clock, label: "tabTemps", Comp: TempsLexique },
  { key: "mythes", icon: Drama, label: "tabMythes", Comp: MythesLexique },
];

export function LexiquesHub() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const Current = LEXIQUES[active].Comp;

  return (
    <section className="relative overflow-hidden" data-testid="lexiques-hub-section">
      <div className="amber-glow" aria-hidden style={{ top: "6%", left: "50%", transform: "translateX(-50%)", width: "780px", height: "540px", opacity: 0.85 }} />
      <div className="max-w-4xl mx-auto px-6 pt-16 sm:pt-20 pb-2 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-9"
        >
          <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">
            <Sparkles size={14} /> {t("landing.lexiquesHub.eyebrow")}
          </p>
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.08] tracking-tight mb-4" data-testid="lexiques-hub-title">
            {t("landing.lexiquesHub.title")}
          </h2>
          <p className="text-foreground/55 italic max-w-md mx-auto">{t("landing.lexiquesHub.subtitle")}</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-2.5 sm:gap-3 mb-2" role="tablist" data-testid="lexiques-tablist">
          {LEXIQUES.map((l, i) => {
            const Icon = l.icon;
            const on = active === i;
            return (
              <motion.button
                key={l.key}
                onClick={() => setActive(i)}
                data-testid={`lexiques-tab-${l.key}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.96 }}
                aria-selected={on}
                role="tab"
                className={`group card-sheen relative flex flex-col items-center justify-center gap-2.5 h-24 sm:h-28 rounded-2xl border px-2.5 text-center overflow-hidden transition-colors duration-300 ${
                  on
                    ? "border-primary/50 bg-primary/[0.08] text-primary shadow-[0_12px_44px_-14px_hsl(28_96%_56%_/_0.6)]"
                    : "border-white/10 bg-white/[0.02] text-foreground/60 hover:border-primary/30 hover:text-primary/90"
                }`}
              >
                {on && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{ background: "radial-gradient(circle at 50% 28%, hsl(28 96% 56% / 0.18), transparent 68%)" }}
                  />
                )}
                <span
                  className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border transition-colors duration-300 ${
                    on ? "border-primary/50 bg-primary/15" : "border-white/10 bg-white/[0.03] group-hover:border-primary/30"
                  }`}
                >
                  <Icon size={18} strokeWidth={1.7} className={on ? "pulse-star" : ""} />
                </span>
                <span className="relative z-10 text-[11px] sm:text-xs font-medium leading-tight line-clamp-2">
                  {t(`landing.lexiquesHub.${l.label}`)}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4 }}
        >
          <Current embedded />
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
